import React, { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, Select, Typography, Button, Checkbox } from '@crisellevaldez/speculo-ui';
import {
  crmSetupSchema,
  CRMSetupData,
  CRMEntryData,
  CRM_OPTIONS,
} from '../schemas/onboarding.schema';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { InfoNotice } from '../components/InfoNotice';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { Organization } from '../../../../types/organization.types';
import { IntegrationsModal } from '../../client/IntegrationsModal';
import { isCrmConnectedViaRollout } from '../../../../utils/rollout';
import { ExternalLink, Plus, X } from 'lucide-react';

interface Stage5CRMSetupProps {
  onNext: (data: CRMSetupData) => void;
  initialData?: Partial<CRMSetupData>;
  isLoading?: boolean;
  organization?: Organization;
  onFubSuccess?: () => void;
}

const CRM_AI_DISCLAIMER_COPY =
  'By checking the box and connecting your CRM and/or submitting data, you acknowledge and agree that you are solely responsible for ensuring that any contacts made available for outreach have provided appropriate consent to be contacted via automated systems, including AI-powered voice calls, dialing technology, text messaging, and email communications.\n\nYou further confirm that you have implemented appropriate tagging, filtering, and/or segmentation within your CRM to identify such contacts, and that only eligible contacts will be engaged by the system in compliance with all applicable laws, regulations, and communication consent requirements.';

const DEFAULT_CRM_ENTRY: CRMEntryData = {
  crm_type: '',
  crm_other_name: '',
  ai_disclaimer_acknowledged: false,
  super_admin_api_key: '',
  login_url: '',
  super_admin_username: '',
  super_admin_password: '',
  two_fa_contact: '',
  brivity_website_login_url: '',
  brivity_username: '',
  brivity_password: '',
};

export const Stage5CRMSetup: React.FC<Stage5CRMSetupProps> = ({
  onNext,
  initialData,
  isLoading = false,
  organization,
  onFubSuccess,
}) => {
  const [isFubModalOpen, setIsFubModalOpen] = useState(false);
  const [isCincModalOpen, setIsCincModalOpen] = useState(false);
  const [isBrivityModalOpen, setIsBrivityModalOpen] = useState(false);
  const [_activeFubIndex, setActiveFubIndex] = useState<number | null>(null);
  const { sendWebhook } = useOnboardingWebhook();

  const form = useForm<CRMSetupData>({
    resolver: zodResolver(crmSetupSchema),
    defaultValues: initialData?.crm_entries?.length
      ? initialData
      : {
          crm_entries: [{ ...DEFAULT_CRM_ENTRY }],
        },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'crm_entries',
  });

  // Check if FUB / CINC / Brivity is connected via Rollout — handles both
  // the new `credentials[]` shape and the legacy `<crm>.connected` shape.
  const crmSources = {
    crmConnections: organization?.crmConnections,
    rollout: organization?.rollout,
  };
  const isFubConnected = isCrmConnectedViaRollout(crmSources, 'follow-up-boss');
  const isCincConnected = isCrmConnectedViaRollout(crmSources, 'cinc');
  const isBrivityConnected = isCrmConnectedViaRollout(crmSources, 'brivity');

  const handleSubmit = async (data: CRMSetupData) => {
    sendWebhook('Stage 5 - Integrations (CRM Setup)', data);
    onNext(data);
  };

  const handleFubSetupClick = (index: number) => {
    setActiveFubIndex(index);
    setIsFubModalOpen(true);
  };

  const handleAddCRM = () => {
    append({ ...DEFAULT_CRM_ENTRY });
  };

  const handleRemoveCRM = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Check if any FUB entry exists and is not connected via Rollout
  const hasPendingFub = fields.some((_, index) => {
    const crmType = form.watch(`crm_entries.${index}.crm_type`);
    return crmType === 'fub' && !isFubConnected;
  });

  // Check if FUB is selected in any entry (to disable adding more CRMs)
  const hasFubSelected = fields.some((_, index) => {
    const crmType = form.watch(`crm_entries.${index}.crm_type`);
    return crmType === 'fub';
  });

  return (
    <StageContainer
      title="CRM Integration"
      description="Connect your CRMs to sync contacts and activities with Speculo AI. You can add multiple CRMs if needed."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8" noValidate>
        {/* Info Notice */}
        <InfoNotice>
          Only Follow Up Boss users can set up their own integrations directly. For all other CRMs,
          please provide the required information and our team will complete the setup.
        </InfoNotice>

        {/* CRM Entries */}
        {fields.map((field, index) => {
          const selectedCrm = form.watch(`crm_entries.${index}.crm_type`);
          const isFub = selectedCrm === 'fub';
          const isCinc = selectedCrm === 'cinc';
          const isBrivity = selectedCrm === 'brivity';
          const isRolloutCrm = isFub || isCinc || isBrivity;
          const isOther = selectedCrm === 'other';
          const isOtherCrm = selectedCrm && !isRolloutCrm;
          const isAIDisclaimerAcknowledged = Boolean(
            form.watch(`crm_entries.${index}.ai_disclaimer_acknowledged`)
          );

          return (
            <div key={field.id} className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b pb-2">
                <Typography variant="h2" className="text-xl font-bold text-gray-900">
                  CRM {index + 1}
                </Typography>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveCRM(index)}
                    className="inline-flex items-center gap-1 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <X className="w-3 h-3" />
                    Remove
                  </Button>
                )}
              </div>

              {/* CRM Selection */}
              <FormSection>
                <Form.Control required>
                  <QuestionLabel>What is the name of your primary CRM?</QuestionLabel>
                  <Controller
                    control={form.control}
                    name={`crm_entries.${index}.crm_type`}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={CRM_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                        placeholder="Select your CRM"
                        error={form.formState.errors.crm_entries?.[index]?.crm_type?.message}
                        searchable
                      />
                    )}
                  />
                </Form.Control>
              </FormSection>

              {/* Other CRM Name */}
              {isOther && (
                <FormSection>
                  <Form.Control required>
                    <QuestionLabel>
                      If "Other" was selected above, enter the name of your CRM:
                    </QuestionLabel>
                    <Input
                      {...form.register(`crm_entries.${index}.crm_other_name`)}
                      placeholder="e.g., Salesforce, HubSpot, etc."
                      error={form.formState.errors.crm_entries?.[index]?.crm_other_name?.message}
                    />
                  </Form.Control>
                </FormSection>
              )}

              {selectedCrm && (
                <FormSection>
                  <Controller
                    control={form.control}
                    name={`crm_entries.${index}.ai_disclaimer_acknowledged`}
                    render={({ field: { value, onChange } }) => (
                      <div className="space-y-2">
                        <div className="p-4 border border-amber-200 dark:border-amber-500/30 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`crm-ai-disclaimer-${index}`}
                              checked={Boolean(value)}
                              onChange={(e) => onChange(e.target.checked)}
                            />
                            <label
                              htmlFor={`crm-ai-disclaimer-${index}`}
                              className="text-xs text-foreground/80 leading-relaxed cursor-pointer whitespace-pre-line"
                            >
                              {CRM_AI_DISCLAIMER_COPY}
                            </label>
                            <span className="text-red-500 text-sm shrink-0">*</span>
                          </div>
                        </div>
                        {form.formState.errors.crm_entries?.[index]?.ai_disclaimer_acknowledged && (
                          <p className="text-sm text-red-600">
                            {
                              form.formState.errors.crm_entries?.[index]?.ai_disclaimer_acknowledged
                                ?.message
                            }
                          </p>
                        )}
                      </div>
                    )}
                  />
                </FormSection>
              )}

              {/* FUB-specific UI - Now using Rollout OAuth */}
              {isFub && isAIDisclaimerAcknowledged && (
                <FormSection>
                  <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <img src="/fub-icon.png" alt="Follow Up Boss" className="w-10 h-10" />
                    <div className="flex-1">
                      <Typography variant="h3" className="text-blue-900 mb-2">
                        Follow Up Boss Integration
                      </Typography>

                      {isFubConnected && (
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-green-700 text-sm font-medium">
                            Follow Up Boss is connected
                          </span>
                        </div>
                      )}

                      <Button
                        type="button"
                        variant={isFubConnected ? 'outline' : 'primary'}
                        size="sm"
                        onClick={() => handleFubSetupClick(index)}
                        disabled={!isAIDisclaimerAcknowledged}
                        className="inline-flex items-center gap-2 py-2.5"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {isFubConnected ? 'Update Configuration' : 'Connect Follow Up Boss'}
                      </Button>
                    </div>
                  </div>
                  <TipBox>
                    <strong>Pro tip:</strong> Before connecting your API, we highly recommend
                    applying the <code className="bg-yellow-200 px-1 rounded">speculo_sync</code>{' '}
                    tag to the contacts you want Remi (AI) to engage with to help speed up the
                    initial sync process especially for large databases. This tag does not trigger
                    any communication. You can still move forward without applying the tag.
                    Additional contacts can be synced over at any time after the integration is
                    established.
                  </TipBox>
                </FormSection>
              )}

              {/* CINC-specific UI - Rollout OAuth */}
              {isCinc && isAIDisclaimerAcknowledged && (
                <FormSection>
                  <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex-1">
                      <Typography variant="h3" className="text-blue-900 mb-2">
                        CINC Integration
                      </Typography>

                      {isCincConnected && (
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-green-700 text-sm font-medium">
                            CINC is connected
                          </span>
                        </div>
                      )}

                      <Button
                        type="button"
                        variant={isCincConnected ? 'outline' : 'primary'}
                        size="sm"
                        onClick={() => setIsCincModalOpen(true)}
                        disabled={!isAIDisclaimerAcknowledged}
                        className="inline-flex items-center gap-2 py-2.5"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {isCincConnected ? 'Update Configuration' : 'Connect CINC'}
                      </Button>
                    </div>
                  </div>
                </FormSection>
              )}

              {/* Brivity-specific UI - Rollout OAuth */}
              {isBrivity && isAIDisclaimerAcknowledged && (
                <FormSection>
                  <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex-1">
                      <Typography variant="h3" className="text-blue-900 mb-2">
                        Brivity Integration
                      </Typography>

                      {isBrivityConnected && (
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-green-700 text-sm font-medium">
                            Brivity is connected
                          </span>
                        </div>
                      )}

                      <Button
                        type="button"
                        variant={isBrivityConnected ? 'outline' : 'primary'}
                        size="sm"
                        onClick={() => setIsBrivityModalOpen(true)}
                        disabled={!isAIDisclaimerAcknowledged}
                        className="inline-flex items-center gap-2 py-2.5"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {isBrivityConnected ? 'Update Configuration' : 'Connect Brivity'}
                      </Button>
                    </div>
                  </div>
                </FormSection>
              )}

              {/* Non-FUB CRM Configuration */}
              {isOtherCrm && isAIDisclaimerAcknowledged && (
                <>
                  {/* API Key */}
                  <FormSection>
                    <Form.Control>
                      <QuestionLabel required={false}>
                        Account Owner (Owner/Super Admin) API Key
                      </QuestionLabel>
                      <Input
                        {...form.register(`crm_entries.${index}.super_admin_api_key`)}
                        placeholder="Enter your API key from the account owner"
                        error={
                          form.formState.errors.crm_entries?.[index]?.super_admin_api_key?.message
                        }
                      />
                      <TipBox>
                        <strong>Note:</strong> You must use the account owner (main admin)
                        credentials. This is the primary admin account with full CRM access. If your
                        CRM provides an API key, enter it here.
                      </TipBox>
                    </Form.Control>
                  </FormSection>

                  {/* Fallback Credentials Section */}
                  <div className="space-y-4">
                    <Typography variant="h3" className="text-lg font-semibold text-gray-700">
                      If an API key is not available, please provide the following details:
                    </Typography>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormSection>
                        <Form.Control>
                          <Form.Label>Login URL</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.login_url`)}
                            placeholder="Direct link to the CRM login page"
                            error={form.formState.errors.crm_entries?.[index]?.login_url?.message}
                          />
                          <Form.HelperText>Direct link to the CRM login page</Form.HelperText>
                        </Form.Control>
                      </FormSection>

                      <FormSection>
                        <Form.Control>
                          <Form.Label>Account Owner Username</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.super_admin_username`)}
                            placeholder="Main admin account username"
                            error={
                              form.formState.errors.crm_entries?.[index]?.super_admin_username
                                ?.message
                            }
                          />
                          <Form.HelperText>
                            Username for the main admin account (account owner)
                          </Form.HelperText>
                        </Form.Control>
                      </FormSection>

                      <FormSection>
                        <Form.Control>
                          <Form.Label>Account Owner Password</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.super_admin_password`)}
                            type="password"
                            placeholder="Enter password"
                            error={
                              form.formState.errors.crm_entries?.[index]?.super_admin_password
                                ?.message
                            }
                          />
                          <Form.HelperText>Password for the main admin account</Form.HelperText>
                        </Form.Control>
                      </FormSection>

                      <FormSection>
                        <Form.Control>
                          <Form.Label>2FA Contact</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.two_fa_contact`)}
                            placeholder="Name, phone, or email for 2FA codes"
                            error={
                              form.formState.errors.crm_entries?.[index]?.two_fa_contact?.message
                            }
                          />
                          <Form.HelperText>
                            Person who manages Two-Factor Authentication codes
                          </Form.HelperText>
                        </Form.Control>
                      </FormSection>
                    </div>
                  </div>

                  {/* Brivity-specific Section */}
                  {isBrivity && (
                    <div className="space-y-4">
                      <Typography
                        variant="h3"
                        className="text-lg font-semibold text-gray-700 border-t pt-4"
                      >
                        Brivity Website Access (different than CRM)
                      </Typography>

                      <FormSection>
                        <Form.Control required>
                          <Form.Label>Brivity Website Login Link</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.brivity_website_login_url`)}
                            placeholder="e.g., https://yoursite.brivity.com"
                            error={
                              form.formState.errors.crm_entries?.[index]?.brivity_website_login_url
                                ?.message
                            }
                          />
                        </Form.Control>
                      </FormSection>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormSection>
                          <Form.Control required>
                            <Form.Label>Username</Form.Label>
                            <Input
                              {...form.register(`crm_entries.${index}.brivity_username`)}
                              placeholder="Brivity website username"
                              error={
                                form.formState.errors.crm_entries?.[index]?.brivity_username
                                  ?.message
                              }
                            />
                          </Form.Control>
                        </FormSection>

                        <FormSection>
                          <Form.Control required>
                            <Form.Label>Password</Form.Label>
                            <Input
                              {...form.register(`crm_entries.${index}.brivity_password`)}
                              type="password"
                              placeholder="Brivity website password"
                              error={
                                form.formState.errors.crm_entries?.[index]?.brivity_password
                                  ?.message
                              }
                            />
                          </Form.Control>
                        </FormSection>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* Add CRM Button - Hidden when FUB is selected */}
        {!hasFubSelected && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleAddCRM}
            className="inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Another CRM
          </Button>
        )}

        {/* Submit Button - Only show when no pending FUB setup */}
        {!hasPendingFub && <NextButton isLoading={isLoading} />}

        {/* FUB instruction when not configured */}
        {hasPendingFub && (
          <div className="text-center py-4">
            <Typography variant="p" className="text-gray-500">
              Please complete the Follow Up Boss setup above to continue.
            </Typography>
          </div>
        )}
      </form>

      {/* FUB Modal */}
      <IntegrationsModal
        isOpen={isFubModalOpen}
        onClose={() => {
          setIsFubModalOpen(false);
          setActiveFubIndex(null);
        }}
        selectedOrg={organization}
        onSuccess={() => {
          if (onFubSuccess) onFubSuccess();
        }}
        crmFilter="fub"
        hideTwilio={true}
        requireAIAcknowledgement={false}
      />

      {/* CINC Modal */}
      <IntegrationsModal
        isOpen={isCincModalOpen}
        onClose={() => setIsCincModalOpen(false)}
        selectedOrg={organization}
        onSuccess={() => {
          if (onFubSuccess) onFubSuccess();
        }}
        crmFilter="cinc"
        hideTwilio={true}
        requireAIAcknowledgement={false}
      />

      {/* Brivity Modal */}
      <IntegrationsModal
        isOpen={isBrivityModalOpen}
        onClose={() => setIsBrivityModalOpen(false)}
        selectedOrg={organization}
        onSuccess={() => {
          if (onFubSuccess) onFubSuccess();
        }}
        crmFilter="brivity"
        hideTwilio={true}
        requireAIAcknowledgement={false}
      />
    </StageContainer>
  );
};
